<template>
  <Modal
    class="scoped"
    content-class
    full-height
    :modal-style="{ maxWidth: '100%' }"
    :title="title"
  >
    <div
      v-if="showPlayer"
      class="mention-review-media-player"
    >
      <MentionReviewMediaPlayer
        :clip="currentClip"
        :type="playerMentionType"
        @toggle-player="showPlayer = !showPlayer"
      />
    </div>
    <QTable
      v-model:selected="selected"
      :columns="columns"
      flat
      hide-pagination
      :pagination="{ rowsPerPage: 0 }"
      row-key="id"
      :rows="mentionReviews"
      selection="multiple"
    >
      <template #body="props">
        <QTr :props="props">
          <QTd class="wrap-table-content vertical-top">
            <QCheckbox v-model="props.selected" />
          </QTd>
          <QTd
            key="id"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            {{ props.row.id }}
          </QTd>

          <QTd
            key="target_id"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            <span v-if="props.row.object">
              {{ props.row.object.id }}<br />
              {{ props.row.type }}
            </span>
          </QTd>

          <QTd
            key="source"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            <span v-if="props.row.object">
              <img
                :src="props.row.object.source.logo_url"
                style="height: 30px"
              />
              {{ props.row.object.source.name }}
              {{ props.row.object.source.location }}
              <span
                v-if="props.row.object && props.row.object.siblings_count > 0"
              >
                +{{ props.row.object.siblings_count }}
              </span>
            </span>
          </QTd>

          <QTd
            key="time"
            class="wrap-table-content vertical-top"
          >
            {{ timeAgo(props.row.target_timestamp) }}
          </QTd>

          <QTd
            key="text"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            <!-- eslint-disable vue/no-v-html -->
            <span v-html="props.row.highlighted_text" />
            <!-- eslint-enable vue/no-v-html -->
          </QTd>

          <QTd
            v-if="props.row.object"
            key="actions"
            class="wrap-table-content vertical-top actions"
            :props="props"
          >
            <QBtn
              flat
              icon="play_circle_outline"
              @click="playMediaForReview(props.row)"
            >
              &nbsp; Play
            </QBtn>
          </QTd>
          <QTd
            v-else
            class="wrap-table-content vertical-top actions"
          >
            <div class="q-pa-sm bg-warning">
              Mention review target not found
            </div>
          </QTd>
        </QTr>
      </template>
    </QTable>

    <template #buttons>
      <BaseButton
        color="primary"
        label="Apply to selected"
        new-design
        @click="save"
      />
    </template>
  </Modal>
</template>

<script>
import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import { timeAgo } from "shared/helpers/date";

import MentionReviewMediaPlayer from "src/components/mention_reviews/MentionReviewMediaPlayer.vue";

export default {
  name: "BulkUpdateMentionReviewsModal",
  components: {
    BaseButton,
    MentionReviewMediaPlayer,
    Modal,
  },
  props: {
    mentionReviews: {
      type: Array,
      required: true,
    },
    params: {
      type: Object,
      required: true,
    },
  },
  emits: ["confirm"],
  data() {
    const columns = [
      {
        name: "id",
        required: true,
        label: "Id",
        align: "center top",
        field: "id",
        style: "width: 5%",
      },
      {
        name: "target_id",
        label: "Mention",
        align: "center top",
        field: "target_id",
        style: "width: 7%",
      },
      {
        name: "source",
        label: "Source",
        align: "left",
        field: "source",
        style: "width: 5%",
      },
      {
        name: "target_timestamp",
        label: "Time",
        align: "left",
        field: "target_timestamp",
        style: "width: 5%",
        classes: "wrap-table-content",
      },
      {
        name: "text",
        required: true,
        label: "Text",
        align: "left",
        field: "text",
        style: "width: 30%;",
      },
    ];

    return {
      columns: [
        ...columns,
        {
          name: "actions",
          required: true,
          label: "Actions",
          align: "left top",
          classes: "wrap-table-content",
          style: "width: 20%",
        },
      ],
      selected: [],
      currentReview: null,
      showPlayer: false,
    };
  },
  computed: {
    title() {
      if (this.params.isApproved) {
        return "Approve Mention Reviews";
      }

      if (this.params.isAdvert) {
        return "Mark Mention Reviews as Advert";
      }

      let title = "Reject Mention reviews";

      if (this.params.isAllOrganisations) {
        title += " for all organisations";
      }

      return title;
    },
  },
  watch: {
    mentionReviews: {
      immediate: true,
      deep: true,
      handler(mentionReviews) {
        this.selected = mentionReviews;
      },
    },
  },
  methods: {
    timeAgo,
    playMediaForReview(review) {
      this.showPlayer = false;
      this.currentReview = review;

      try {
        if (review.type === "radio_clip") {
          this.loadAndPlayRadioClip(review);
        } else {
          this.loadAndPlayVideoClip(review);
        }
      } catch {
        this.$q.notify({
          message: "Could not play clips",
          color: "warning",
          position: "center",
          closeBtn: "Dismiss",
        });
      }
    },
    async loadAndPlayRadioClip(review) {
      this.currentClip = (
        await this.$streemApiAdmin.get(`radio_clips/${review.object.id}`)
      ).data;

      this.currentClip.offset = 0;
      this.playerMentionType = "radio";
      this.showPlayer = true;
    },
    async loadAndPlayVideoClip(review) {
      const mentionedAtTime = this.firstMentionTime(review);

      const tvClips = (
        await this.$streemApiAdmin.get(
          `tv_channels/${review.object.source.id}/clips`,
          {
            params: {
              after: mentionedAtTime - 30,
              before: mentionedAtTime + 30,
            },
          }
        )
      ).data;

      let clips = tvClips.filter(
        (clip) =>
          clip.start_time <= mentionedAtTime && clip.end_time > mentionedAtTime
      );

      if (clips.length === 0) {
        clips = tvClips;
      }

      this.currentClip = (
        await this.$streemApiAdmin.get(`tv_clips/${clips[0].id}`)
      ).data;

      this.currentClip.offset = Math.max(
        mentionedAtTime - this.currentClip.start_time - 2,
        0
      );

      this.playerMentionType = "tv";
      this.showPlayer = true;
    },
    firstMentionTime(review) {
      if (this.type === "radio" || review.type === "tv_logo_appearance") {
        return review.object.start_time;
      }

      const keyword = review.highlighted_text
        .match("<em>(.*?)</em>")[1]
        .toLowerCase();

      const index = review.object.fragments.findIndex(
        (fragment) =>
          fragment.text.toLowerCase().search(keyword.toLowerCase()) !== -1
      );

      return index !== -1
        ? review.object.fragments[index].timestamp
        : review.target_timestamp / 1000;
    },
    save() {
      this.$emit("confirm", this.selected);
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.q-table) {
  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
  }
}

:deep(.q-table__middle) {
  height: 100%;
}

table img {
  max-width: 100%;
  max-height: 96px;
}

table .wrap-table-content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: initial;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: 14px;
}

.actions {
  button.q-btn {
    text-transform: none;
    font-size: 12px;
    margin-top: 10px;
  }
}

:deep(em) {
  background-color: #fc0 !important;
  padding: 1px 2px !important;
  font-style: normal !important;
}

.mention-review-media-player {
  position: fixed;
  z-index: 9999;
  top: 5px;
  right: 25%;
  box-shadow: 4px 7px 8px 4px #ccc;
  background: #fff;
}
</style>
